import { io } from "socket.io-client";
import { config, getSocketIoServerUrl } from "@/app/config";
import { EVENTS } from "@/constants/events";
import { useAuthStore } from "@/store/auth.store.js";
import { createIntegrationLogger } from "@/lib/app-log.js";

let socket = null;
const listeners = new Map();
const wsLog = createIntegrationLogger("ws");

function notify(event, payload) {
  const set = listeners.get(event);
  if (!set) return;
  set.forEach((handler) => {
    try {
      handler(payload);
    } catch (e) {
      wsLog.error("Listener failed", { event, error: e?.message });
    }
  });
}

/**
 * Prefer in-memory auth (Zustand); fall back to persisted storage before rehydration completes.
 */
function getSessionToken() {
  const fromStore = useAuthStore.getState().token;
  if (fromStore) return fromStore;
  try {
    const raw = localStorage.getItem("auth-storage") || "{}";
    const authState = JSON.parse(raw);
    return authState?.state?.token || null;
  } catch {
    return null;
  }
}

function authTokenForSocket(instance) {
  const a = instance?.auth;
  if (a && typeof a === "object" && "token" in a) return a.token;
  return instance?.io?.opts?.auth?.token ?? null;
}

function attachSocketHandlers(instance) {
  instance.on(EVENTS.NOTIFICATION_RECEIVED, (payload) => {
    notify(EVENTS.NOTIFICATION_RECEIVED, payload);
  });
  instance.on("reference_invalidated", (payload) => {
    notify("reference_invalidated", payload);
  });
  instance.on("my_work_invalidated", (payload) => {
    notify("my_work_invalidated", payload);
  });
  instance.on("disconnect", (reason) => {
    wsLog.debug("disconnect", { reason });
    notify("disconnect", { reason });
  });
  instance.on("connect", () => {
    wsLog.debug("connect", { id: instance.id, transport: instance.io?.engine?.transport?.name });
    notify("connect", {});
  });
  instance.on("connect_error", (err) => {
    wsLog.debug("connect_error", { message: err?.message });
  });
  instance.io?.on?.("reconnect_attempt", (attempt) => {
    wsLog.debug("reconnect_attempt", { attempt });
  });
  instance.io?.on?.("reconnect", (attempt) => {
    wsLog.debug("reconnect", { attempt });
  });
  instance.io?.on?.("reconnect_error", (err) => {
    wsLog.debug("reconnect_error", { message: err?.message });
  });
  instance.io?.on?.("reconnect_failed", () => {
    wsLog.debug("reconnect_failed", {});
  });
}

/**
 * Single Socket.IO client for the SPA. Prefer one connection from useAppInit after auth.
 * Reconnects when the session token changes (login / refresh) so the handshake JWT stays valid.
 *
 * @returns {import("socket.io-client").Socket | null}
 */
export function connectWebSocket() {
  if (!config.ws.enabled) return null;

  const token = getSessionToken();

  if (!token) {
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    return null;
  }

  if (socket) {
    const sameToken = authTokenForSocket(socket) === token;
    if (sameToken && (socket.connected || socket.active)) {
      return socket;
    }
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(getSocketIoServerUrl(), {
    auth: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: config.ws.reconnectInitialMs,
    reconnectionDelayMax: config.ws.reconnectMaxMs,
  });

  attachSocketHandlers(socket);
  return socket;
}

/** @returns {import("socket.io-client").Socket | null} */
export function getSocket() {
  return socket;
}

export function disconnectWebSocket() {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

export function subscribe(event, handler) {
  const set = listeners.get(event) || new Set();
  set.add(handler);
  listeners.set(event, set);
  return () => {
    set.delete(handler);
    if (set.size === 0) listeners.delete(event);
  };
}

export { EVENTS };
